package com.elan.edubridge.ui.staff

import com.elan.edubridge.shared.di.SharedGraph
import com.elan.edubridge.shared.domain.model.AmendAttendanceRequest
import com.elan.edubridge.shared.domain.model.AttendanceEvent
import com.elan.edubridge.shared.domain.model.AttendanceEventType
import com.elan.edubridge.shared.domain.model.AttendanceStatus
import com.elan.edubridge.shared.domain.model.DailyRosterEntry
import com.elan.edubridge.shared.domain.model.RecordAttendanceRequest
import com.elan.edubridge.shared.domain.model.SessionState
import com.elan.edubridge.ui.shell.escapeHtml
import com.elan.edubridge.ui.shell.icon
import kotlinx.browser.document
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLTextAreaElement

/**
 * "출석 처리" 배치 모달 + "이벤트 취소"(C3 AMEND) 확인 모달(가이드 §3.4·§3.6 "eb-modal 패턴").
 *
 * 둘 다 `document.body`에 직접 마운트한다(Toast와 같은 이유 — 라우트 재렌더에 휩쓸리지 않기 위해)
 * 그리고 자기 자신을 정리(remove)한 뒤 콜백으로 호출부에 제어를 돌려준다.
 */
object AttendanceModal {
    private val scope = MainScope()
    private val commandRepository = SharedGraph.createAcademyCommandRepository()
    private val getClassRoster = SharedGraph.createGetClassRosterUseCase()

    /** "출석 처리" 클릭 → 선택된 첫 수업의 로스터를 불러와 상태 토글 모달을 연다. */
    fun openRoster(
        session: SessionState,
        academyId: String,
        classId: String,
        className: String,
        serviceDate: String,
        onSaved: () -> Unit,
    ) {
        scope.launch {
            val roster = getClassRoster(session, academyId, classId, serviceDate)
            showRosterModal(academyId, classId, className, serviceDate, roster, onSaved)
        }
    }

    /** "⋯ → 이벤트 취소" 클릭 → 사유를 필수 입력받고 confirm 시 AMEND 이벤트를 append한다. */
    fun openCancelConfirm(academyId: String, event: AttendanceEvent, onAmended: () -> Unit) {
        val backdrop = mount("""
            <div class="eb-modal" role="dialog" aria-modal="true" aria-labelledby="cancel-modal-title">
                <div class="eb-modal__header">
                    <h2 id="cancel-modal-title">이벤트 취소</h2>
                    <button class="eb-icon-button" id="modal-close" aria-label="닫기">${icon("icon-close", "md")}</button>
                </div>
                <div class="eb-modal__body">
                    <p class="eb-meta">${event.studentName.escapeHtml()} 학생의 ${eventTypeLabel(event.type)} 기록을 취소해요. 기록은 지워지지 않고 취소됨으로 표시돼요.</p>
                    <div class="eb-field">
                        <label for="cancel-reason">취소 사유</label>
                        <textarea id="cancel-reason" class="eb-textarea" required placeholder="예: 중복 스캔"></textarea>
                    </div>
                    <p class="eb-form-error" role="alert" id="cancel-error" hidden></p>
                </div>
                <div class="eb-modal__footer">
                    <button class="eb-button eb-button--ghost" id="modal-cancel">닫기</button>
                    <button class="eb-button eb-button--primary" id="modal-confirm">이벤트 취소</button>
                </div>
            </div>
        """.trimIndent())

        fun close() = backdrop.remove()
        (backdrop.querySelector("#modal-close") as? HTMLElement)?.onclick = { close(); null }
        (backdrop.querySelector("#modal-cancel") as? HTMLElement)?.onclick = { close(); null }
        backdrop.onclick = { event2 -> if (event2.target == backdrop) close(); null }

        (backdrop.querySelector("#modal-confirm") as? HTMLButtonElement)?.onclick = {
            val reasonInput = backdrop.querySelector("#cancel-reason") as HTMLTextAreaElement
            val reason = reasonInput.value.trim()
            val errorEl = backdrop.querySelector("#cancel-error") as HTMLElement
            if (reason.isEmpty()) {
                errorEl.textContent = "취소 사유를 입력해 주세요."
                errorEl.removeAttribute("hidden")
            } else {
                scope.launch {
                    // CHECK_IN 취소 → 아직 등원 전(NOT_ARRIVED)으로, CHECK_OUT 취소 → 아직 있음(PRESENT)으로 되돌린다.
                    // 서버가 최종 판정 주체이며(InMemory 스텁은 값을 검증하지 않는다), 이 매핑은 UI 표시상의 합리적 기본값이다.
                    val revertStatus = if (event.type == AttendanceEventType.CHECK_OUT) {
                        AttendanceStatus.PRESENT
                    } else {
                        AttendanceStatus.NOT_ARRIVED
                    }
                    val result = commandRepository.amendAttendance(
                        AmendAttendanceRequest(
                            academyId = academyId,
                            previousEventId = event.eventId,
                            status = revertStatus,
                            reason = reason,
                            idempotencyKey = "amend_${event.eventId}",
                        ),
                    )
                    close()
                    if (result.isSuccess) {
                        Toast.success("출결 이벤트를 취소했어요.")
                        onAmended()
                    } else {
                        Toast.error(result.errorMessage ?: "취소에 실패했어요. 다시 시도해 주세요.")
                    }
                }
            }
            null
        }
    }

    private fun showRosterModal(
        academyId: String,
        classId: String,
        className: String,
        serviceDate: String,
        roster: List<DailyRosterEntry>,
        onSaved: () -> Unit,
    ) {
        val pendingChanges = mutableMapOf<String, AttendanceStatus>()

        if (roster.isEmpty()) {
            Toast.error("$className 명단을 찾을 수 없어요.")
            return
        }

        val rows = roster.joinToString("") { entry ->
            """
            <div class="eb-roster-row" data-student-id="${entry.studentId}">
                <span>${entry.studentName.escapeHtml()}</span>
                <div class="eb-roster-row__status">
                    ${statusToggle(entry.studentId, AttendanceStatus.PRESENT, "출석", entry.status)}
                    ${statusToggle(entry.studentId, AttendanceStatus.LATE, "지각", entry.status)}
                    ${statusToggle(entry.studentId, AttendanceStatus.ABSENT, "결석", entry.status)}
                </div>
            </div>
            """.trimIndent()
        }

        val backdrop = mount("""
            <div class="eb-modal" role="dialog" aria-modal="true" aria-labelledby="roster-modal-title">
                <div class="eb-modal__header">
                    <h2 id="roster-modal-title">${className.escapeHtml()} 출석 처리</h2>
                    <button class="eb-icon-button" id="modal-close" aria-label="닫기">${icon("icon-close", "md")}</button>
                </div>
                <div class="eb-modal__body" id="roster-rows">$rows</div>
                <div class="eb-modal__footer">
                    <button class="eb-button eb-button--ghost" id="modal-cancel">취소</button>
                    <button class="eb-button eb-button--primary" id="modal-confirm">저장</button>
                </div>
            </div>
        """.trimIndent())

        fun close() = backdrop.remove()
        (backdrop.querySelector("#modal-close") as? HTMLElement)?.onclick = { close(); null }
        (backdrop.querySelector("#modal-cancel") as? HTMLElement)?.onclick = { close(); null }
        backdrop.onclick = { event -> if (event.target == backdrop) close(); null }

        val toggles = backdrop.querySelectorAll(".eb-status-toggle")
        for (index in 0 until toggles.length) {
            val toggle = toggles.item(index) as? HTMLButtonElement ?: continue
            toggle.onclick = {
                // data-student-id는 이 버튼을 만든 statusToggle()이 항상 채운다 — null이면 렌더 쪽 버그다.
                val studentId = toggle.getAttribute("data-student-id")!!
                val status = AttendanceStatus.valueOf(toggle.getAttribute("data-status")!!)
                pendingChanges[studentId] = status
                // studentId를 CSS 속성 선택자 문자열에 그대로 끼워 넣으면 특수문자(따옴표 등)로
                // 선택자가 깨지거나 의도치 않은 매칭이 생길 수 있다 — closest()로 같은 행을
                // DOM 트리 탐색만으로 찾으면 애초에 문자열 조립이 필요 없다.
                val row = toggle.closest(".eb-roster-row") as? HTMLElement
                row?.querySelectorAll(".eb-status-toggle")?.let { siblings ->
                    for (i in 0 until siblings.length) {
                        (siblings.item(i) as? HTMLElement)?.classList?.remove("eb-status-toggle--active")
                    }
                }
                toggle.classList.add("eb-status-toggle--active")
                null
            }
        }

        (backdrop.querySelector("#modal-confirm") as? HTMLButtonElement)?.onclick = {
            if (pendingChanges.isEmpty()) {
                close()
                onSaved()
            } else {
                scope.launch {
                    val requests = pendingChanges.map { (studentId, status) ->
                        RecordAttendanceRequest(
                            academyId = academyId,
                            classId = classId,
                            studentId = studentId,
                            serviceDate = serviceDate,
                            status = status,
                            // status를 키에 포함한다 — 안 그러면 같은 학생을 다른 상태로 다시
                            // 제출하는 재시도가 서버 멱등 판정에 걸려 "이전 상태" 결과로 리플레이된다(리뷰 M7).
                            idempotencyKey = "${serviceDate}_${classId}_${studentId}_${status.name}",
                        )
                    }
                    val result = commandRepository.recordAttendanceBatch(requests)
                    close()
                    if (result.isSuccess) {
                        Toast.success("출석을 저장하고 보호자에게 알림을 보냈어요.")
                    } else {
                        Toast.error("${result.failureCount}건 저장에 실패했어요. 다시 시도해 주세요.")
                    }
                    onSaved()
                }
            }
            null
        }
    }

    private fun statusToggle(studentId: String, status: AttendanceStatus, label: String, current: AttendanceStatus): String {
        val variant = when (status) {
            AttendanceStatus.PRESENT -> "present"
            AttendanceStatus.LATE -> "late"
            AttendanceStatus.ABSENT -> "absent"
            else -> "present"
        }
        val active = if (status == current) " eb-status-toggle--active" else ""
        return """<button type="button" class="eb-status-toggle eb-status-toggle--$variant$active" data-student-id="$studentId" data-status="${status.name}">$label</button>"""
    }

    private fun eventTypeLabel(type: AttendanceEventType): String = when (type) {
        AttendanceEventType.CHECK_IN -> "등원"
        AttendanceEventType.CHECK_OUT -> "하원"
        AttendanceEventType.AMEND -> "정정"
    }

    private fun mount(dialogHtml: String): HTMLElement {
        val backdrop = document.createElement("div") as HTMLElement
        backdrop.className = "eb-modal-backdrop"
        backdrop.innerHTML = dialogHtml
        document.body!!.appendChild(backdrop)
        return backdrop
    }
}
